<template>
  <div class="green-operation-historical-history">
    <a
      class="green-operation-historical-history__back-link"
      @click="$emit('back')"
    >
      &lt; {{ $t('tenancy.greenOperation.backToSpp') }}
    </a>
    <HKLCard class="green-operation-historical-history__card">
      <template #header>
        <div class="green-operation-historical-history__row-header">
          {{ $t('tenancy.greenOperation.historicalRecords') }}
        </div>
      </template>
      <GreenOperationStatusInfo />
      <div class="row green-operation-historical-history__sections">
        <HKLCard
          v-if="hasExcludedDocument"
          :class="[
            'green-operation-historical-history__section-card',
            hasHistoricalDetails ? 'small-6' : 'small-12',
          ]"
          :title="$t('tenancy.greenOperation.tab.sustainabilityDocuments')"
        >
          <GreenOperationDocuments
            readonly
            showExcludedOnly
          />
        </HKLCard>
        <div
          v-if="hasHistoricalDetails"
          :class="[
            'green-operation-historical-history__details',
            hasExcludedDocument ? 'small-6' : 'small-12',
          ]"
        >
          <HKLTabs
            v-if="showCommentsTab"
            v-model="activeKey"
            class="shadowline-theme green-operation-historical-history__tabs"
            defaultActiveKey="1"
          >
            <a-tab-pane
              key="1"
              :tab="$t('tenancy.greenOperation.tab.assessment')"
            >
              <HKLCardBody>
                <GreenOperationAssessment readonly />
              </HKLCardBody>
            </a-tab-pane>
            <a-tab-pane key="2">
              <template slot="tab">
                <span>
                  {{ $t('tenancy.greenOperation.tab.comments') }}
                </span>
              </template>
              <div class="green-operation green-operation-comments">
                <GreenOperationHistory />
              </div>
            </a-tab-pane>
          </HKLTabs>
          <HKLCard
            v-else
            class="green-operation-historical-history__section-card"
            :title="$t('tenancy.greenOperation.tab.assessment')"
          >
            <GreenOperationAssessment readonly />
          </HKLCard>
        </div>
      </div>
    </HKLCard>
  </div>
</template>

<script>
import { computed, provide, ref, unref, watch } from 'vue-demi'
import HKLCard from '@/controls/Card/HKLCard.vue'
import HKLCardBody from '@/controls/Card/HKLCardBody.vue'
import HKLTabs from '@/controls/Tab/HKLTabs.vue'
import { useAttachments } from '@/controls/Upload/hook'
import { useStoreGetters } from '@/store/index'
import { DEPENDENCY_KEYS } from './consts'
import GreenOperationAssessment from './GreenOperationAssessment.vue'
import GreenOperationDocuments from './GreenOperationDocuments.vue'
import GreenOperationStatusInfo from './GreenOperationStatusInfo.vue'
import GreenOperationHistory from './letters/GreenOperationHistory.vue'
import { isExcluded } from './GreenOperationDocuments.utils'
import { useGreenOperationLetters } from './GreenOperationLetter.hook'
import { useGreenOperationLegacyScenarioState } from './GreenOperationLegacyScenario.hook'
import { useGreenOperation } from './hook'

export default {
  name: 'GreenOperationHistoricalHistory',
  components: {
    HKLCard,
    HKLCardBody,
    HKLTabs,
    GreenOperationAssessment,
    GreenOperationDocuments,
    GreenOperationHistory,
    GreenOperationStatusInfo,
  },
  setup () {
    const activeKey = ref('1')
    const { isTenant, isHKlandUser } = useStoreGetters('user', ['isTenant', 'isHKlandUser'])
    const { data: greenOperation } = useGreenOperation()
    const greenOperationId = computed(() => unref(greenOperation).id)
    const { data: files } = useAttachments(greenOperationId)
    const { refresh: refreshLetters } = useGreenOperationLetters()
    const { hasCompletedRecognitionLetter } = useGreenOperationLegacyScenarioState()
    const hasAssessment = computed(() => (unref(greenOperation).assessments || []).length > 0)
    const hasExcludedDocument = computed(() => unref(files).some(isExcluded))
    const showCommentsTab = computed(() => {
      return unref(hasCompletedRecognitionLetter) && (unref(isTenant) || unref(isHKlandUser))
    })
    const hasHistoricalDetails = computed(() => unref(hasAssessment) || unref(showCommentsTab))

    provide(DEPENDENCY_KEYS.TAB_TYPE, activeKey)
    watch(() => unref(greenOperation).id, (id) => id && refreshLetters(), { immediate: true })

    return {
      activeKey,
      hasExcludedDocument,
      hasHistoricalDetails,
      showCommentsTab,
    }
  },
}
</script>

<style lang="scss" scoped>
.green-operation-historical-history__card {
  ::v-deep .hkl-card_header.small-12 {
    text-align: left;
  }
}

.green-operation-historical-history__back-link {
  display: inline-block;
  margin-bottom: 10px;
  color: #0c3a58;
  cursor: pointer;
  font-weight: bold;
  text-decoration: none;
}

.green-operation-historical-history__row-header {
  text-align: left;
}

.green-operation-historical-history__sections {
  margin-top: 16px;
}

.green-operation-historical-history__details {
  padding-left: 0;
  padding-right: 0;

  .green-operation-historical-history__section-card {
    width: 100%;
  }
}

.green-operation-historical-history__section-card.small-6 {
  padding-right: 10px;
}

.green-operation-historical-history__details.small-6 {
  padding-left: 10px;
}

.green-operation-historical-history__tabs {
  width: 100%;
}

.hkl-tabs.shadowline-theme {
  ::v-deep {
    .ant-tabs-tab {
      height: 48px;
      line-height: 48px;
      &, &.ant-tabs-tab-active {
        background-color: #fefefe;
      }
    }
  }
}
</style>
